import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as api from "./configurationAPI";

export const getAllConfigurations = createAsyncThunk(
  "configuration/getAll",
  async (_, { rejectWithValue }) => {
    try {
      return await api.fetchAllConfigurations();
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const getConfigurationsByAccountId = createAsyncThunk(
  "configuration/getByAccountId",
  async (accountId, { rejectWithValue }) => {
    try {
      return await api.fetchConfigurationsByAccountId(accountId);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const getConfigurationById = createAsyncThunk(
  "configuration/getById",
  async (uuid, { rejectWithValue }) => {
    try {
      return await api.fetchConfigurationById(uuid);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const getConfigurationByConfigId = createAsyncThunk(
  "configuration/getByConfigId",
  async ({ configType, configId }, { rejectWithValue }) => {
    try {
      return await api.fetchConfigurationByConfigId(configType, configId);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const addConfiguration = createAsyncThunk(
  "configuration/add",
  async (data, { rejectWithValue }) => {
    try {
      return await api.createConfiguration(data);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const editConfiguration = createAsyncThunk(
  "configuration/edit",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      return await api.updateConfiguration(uuid, data);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const removeConfiguration = createAsyncThunk(
  "configuration/remove",
  async (uuid, { rejectWithValue }) => {
    try {
      await api.deleteConfiguration(uuid);
      return uuid;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const editLLMConfiguration = createAsyncThunk(
  "configuration/editLLM",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      return { uuid, model: await api.updateLLMConfiguration(uuid, data) };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const editTranscriber = createAsyncThunk(
  "configuration/editTranscriber",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      return { uuid, transcriber: await api.updateTranscriber(uuid, data) };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const editVoice = createAsyncThunk(
  "configuration/editVoice",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      return { uuid, voice: await api.updateVoice(uuid, data) };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const removeNestedField = createAsyncThunk(
  "configuration/removeNestedField",
  async ({ uuid, field }, { rejectWithValue }) => {
    try {
      await api.deleteNestedField(uuid, field);
      return { uuid, field };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const setConfigurationAsDefault = createAsyncThunk(
  "configuration/setAsDefault",
  async ({ uuid, type }, { rejectWithValue }) => {
    try {
      const res =  await api.setConfigurationAsDefault(uuid, type);
      console.log("Ress",res);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const getDefaultConfiguration = createAsyncThunk(
  "configuration/getDefault",
  async ({ configId, configType, type }, { rejectWithValue }) => {
    try {
      return await api.fetchDefaultConfiguration({
        configId,
        configType,
        type,
      });
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

const configurationSlice = createSlice({
  name: "configuration",
  initialState: {
    configurations: [],
    configuration: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearConfiguration: (state) => {
      state.configuration = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllConfigurations.fulfilled, (state, action) => {
        state.loading = false;
        state.configurations = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(getConfigurationsByAccountId.fulfilled, (state, action) => {
        state.configurations = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(getConfigurationById.fulfilled, (state, action) => {
        state.configuration = action.payload;
      })
      .addCase(getConfigurationByConfigId.fulfilled, (state, action) => {
        state.configuration = action.payload;
      })
      .addCase(addConfiguration.fulfilled, (state, action) => {
        state.configurations.push(action.payload);
      })
      .addCase(editConfiguration.fulfilled, (state, action) => {
        const index = state.configurations.findIndex(
          (c) => c.uuid === action.payload.uuid,
        );
        if (index !== -1) state.configurations[index] = action.payload;
      })
      .addCase(removeConfiguration.fulfilled, (state, action) => {
        state.configurations = state.configurations.filter(
          (c) => c.uuid !== action.payload,
        );
      })
      // Partial Updates
      .addCase(editLLMConfiguration.fulfilled, (state, action) => {
        const { uuid, model } = action.payload;
        const index = state.configurations.findIndex((c) => c.uuid === uuid);
        if (index !== -1) state.configurations[index].model = model;
      })
      .addCase(editTranscriber.fulfilled, (state, action) => {
        const { uuid, transcriber } = action.payload;
        const index = state.configurations.findIndex((c) => c.uuid === uuid);
        if (index !== -1) state.configurations[index].transcriber = transcriber;
      })
      .addCase(editVoice.fulfilled, (state, action) => {
        const { uuid, voice } = action.payload;
        const index = state.configurations.findIndex((c) => c.uuid === uuid);
        if (index !== -1) state.configurations[index].voice = voice;
      })
      .addCase(removeNestedField.fulfilled, (state, action) => {
        const { uuid, field } = action.payload;
        const index = state.configurations.findIndex((c) => c.uuid === uuid);
        if (index !== -1) state.configurations[index][field] = null;
      })
      .addCase(setConfigurationAsDefault.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.configurations.findIndex(
          (c) => c.uuid === updated?.uuid,
        );
        if (index !== -1) state.configurations[index] = updated;

        if (updated?.assistantId) {
          state.configurations = state.configurations.map((c) =>
            c.assistantId === updated.assistantId && c.uuid !== updated.uuid
              ? { ...c, isDefault: false }
              : c,
          );
        }

        if (state.configuration?.uuid === updated?.uuid) {
          state.configuration = updated;
        }
      });
  },
});

export const { clearConfiguration } = configurationSlice.actions;
export default configurationSlice.reducer;
